import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { useQuery, useMutation } from "@apollo/react-hooks";
import classnames from "classnames";
import get from "lodash/get";

import Analytics from "analytics/util/Analytics";
import {
	PROMO_DESIGN,
	READ_RECEIPT_ANALYTICS_PROPS,
} from "analytics/read_receipts.js";
import {
	OKEVENT,
	subscribeToOkEvent,
	unsubscribeFromOkEvent,
} from "shared/util/OkEventHelpers";
import Context from "shared/util/Context";
import ReadReceiptCheckMarks from "shared/components/svgs/read_receipt.svg";
import Str from "shared/components/Str";
import ReadReceiptRateCard from "shared/components/ReadReceiptRateCard";
import { getPlatform } from "shared/payment/services";
import ActivateReadReceipt from "./ActivateReadReceipt";
import "./ReadReceiptCTA.scss";

const QUERY = gql`
	query getReadReceiptTokenCount(
		$userId: String!
	) {
		user(id: $userId) {
			id
			readReceiptTokenCount
		}
	}
`;

const MUTATION = gql`
	mutation activateReadReceipt($correspondentId: ID!, $platform: Platform!) {
		conversationsActivateReadReceipt(
			correspondentId: $correspondentId
			platform: $platform
		) {
			success
		}
	}
`;

const ReadReceiptCTA = ({
	showExplainerText,
	correspondentId,
	setIsReadReceiptActivated,
}) => {
	const [ isRateCardOpen, setIsRateCardOpen ] = useState(false);
	const [ isActivateReadReceiptOpen, setIsActivateReadReceiptOpen ] = useState(false);
	const [ isProcessingActivation, setProcessingActivation ] = useState(false);
	const [ hasSuccessfullyActivated, setHasSuccessfullyActivated ] = useState(false);

	const {
		data,
		refetch,
	} = useQuery(QUERY, {
		variables: {
			userId: Context.get("userid"),
		},
	});

	const [
		onActivateReadReceiptMutation,
		{
			data: activateReadReceiptResp,
		},
	] = useMutation(MUTATION, {
		refetchQueries: ["getReadReceiptTokenCount"],
	});

	useEffect(() => {
		let timer;

		const activateReadReceiptCompletePayload = get(activateReadReceiptResp, "conversationsActivateReadReceipt") || {};

		if ((activateReadReceiptCompletePayload.success)) {
			setProcessingActivation(false);
			setHasSuccessfullyActivated(true);

			timer = setTimeout(() => {
				setIsReadReceiptActivated(correspondentId);
				setHasSuccessfullyActivated(false);
			}, 3000);
		}

		return () => clearTimeout(timer);
	}, [activateReadReceiptResp, correspondentId, setIsReadReceiptActivated]);

	const readReceiptTokenCountGraphQL = get(data, "user.readReceiptTokenCount");
	const promoDesign = showExplainerText ? PROMO_DESIGN.IN_LINE : PROMO_DESIGN.HEADER;

	useEffect(() => {
		subscribeToOkEvent(OKEVENT.ReadReceiptTokensPurchased, refetch);
		return () => unsubscribeFromOkEvent(OKEVENT.ReadReceiptTokensPurchased, refetch);
	}, [refetch]);

	useEffect(() => {
		Analytics.logUIEvent(Analytics.eventNames.viewedFirstPartyPromo, {
			data: {
				"is upsell": true,
				"promo design": promoDesign,
				...READ_RECEIPT_ANALYTICS_PROPS,
			},
		});
	}, [promoDesign, showExplainerText]);

	const formattedTokenCount = (readReceiptTokenCountGraphQL)
		? ` (${readReceiptTokenCountGraphQL})`
		: null;

	function onButtonClick() {
		Analytics.logUIEvent(Analytics.eventNames.selectedFirstPartyPromo, {
			data: {
				"promo design": promoDesign,
				...READ_RECEIPT_ANALYTICS_PROPS,
			},
		});

		return readReceiptTokenCountGraphQL
			? setIsActivateReadReceiptOpen(true)
			: setIsRateCardOpen(true);
	}

	function onActivateReadReceipt() {
		setIsActivateReadReceiptOpen(false);
		setProcessingActivation(true);
		onActivateReadReceiptMutation({
			variables: {
				correspondentId,
				platform: getPlatform(),
			},
		});
	}

	const buttonCTA = isProcessingActivation
		? (
			<Str className="read-receipt-cta-btn-text">
				Processing
			</Str>
		)
		: (
			<React.Fragment>
				<ReadReceiptCheckMarks />
				<Str className="read-receipt-cta-btn-text">
					Activate Read Receipt
				</Str>
				{formattedTokenCount}
			</React.Fragment>
		);

	if (hasSuccessfullyActivated) {
		return (
			<Str className="read-receipt-cta--activated">
				Read Receipt activated
			</Str>
		);
	}

	return (
		<React.Fragment>
			<ReadReceiptRateCard
				isOpen={isRateCardOpen}
				onClose={() => setIsRateCardOpen(false)}
				correspondentId={correspondentId}
				hasInlineCTA={showExplainerText}
			/>
			<div className="read-receipt-cta">
				{showExplainerText && (
					<Str className="read-receipt-cta-explainer-text">
						Want to know if they‘ve read your message?
					</Str>
				)}
				<div className="read-receipt-cta-btn-container">
					<button
						onClick={onButtonClick}
						className={classnames("read-receipt-cta-btn", {
							"read-receipt-cta-btn-processing": isProcessingActivation,
						})}
						disabled={isProcessingActivation}
					>
						{buttonCTA}
					</button>
					{isActivateReadReceiptOpen
						&& (
							<ActivateReadReceipt
								tokenCount={readReceiptTokenCountGraphQL}
								correspondentId={correspondentId}
								onActivateReadReceipt={onActivateReadReceipt}
								onActivateReadReceiptClose={() => setIsActivateReadReceiptOpen(false)}
							/>
						)
					}
				</div>
			</div>
		</React.Fragment>
	);
};

ReadReceiptCTA.propTypes = {
	showExplainerText: PropTypes.bool,
	correspondentId: PropTypes.string.isRequired,
	setIsReadReceiptActivated: PropTypes.func.isRequired,
};

ReadReceiptCTA.defaultProps = {
	showExplainerText: false,
};

export default ReadReceiptCTA;



// WEBPACK FOOTER //
// ./src/apps/messenger/components/ReadReceiptCTA/ReadReceiptCTA.jsx